/**
 * A abertura do score: por que a licitação tirou o número que tirou.
 *
 * Existe porque score que não se abre não se audita. O backend já gravava os
 * cinco termos separados — justamente para isto — e já publicava
 * `GET /api/licitacoes/{id}/score`, mas nenhuma tela chamava a rota. Era um
 * número de 0 a 100 sem nada por trás dele, que é a mesma situação do painel
 * antigo que derivava score de um hash do id.
 *
 * ── Três coisas que o desenho precisa respeitar ──────────────────────────
 *
 * **A contribuição, e não só o termo.** Mostrar "confiança 0,75" sem dizer que
 * ela vale 40 dos 100 pontos não explica nada — quem lê não tem como saber se
 * 0,75 pesou muito ou pouco. Por isso a coluna da direita, e por isso os pesos
 * vêm do servidor: são linhas de `config`, mudam pela tela de configuração, e
 * uma cópia aqui divergiria na primeira recalibração.
 *
 * **Nulo não é zero, em nenhum dos cinco.** `confiança` nula é a triagem ter
 * respondido "baixa", que o escopo define como "na dúvida, deixe a pessoa
 * decidir"; zero seria a IA ter dito não. Os dois aparecem diferentes, e o
 * termo nulo diz que saiu da conta em vez de mostrar um traço mudo.
 *
 * **O peso apurado é a informação mais importante da tela quando falta.** Um
 * score 100 apurado sobre 55 dos 100 pontos não vira `participar`, e sem esta
 * seção isso apareceria como contradição — nota máxima marcada "avaliar", sem
 * explicação. O aviso de evidência parcial é o que impede a tela de parecer
 * quebrada exatamente no caso em que ela está certa.
 */
import { useQuery } from "@tanstack/react-query";
import { api, ErroApi, type CriterioDoScore } from "../api/cliente";
import { SeloNaoAtestado } from "./Selos";
import css from "./PainelScore.module.css";

/** A ordem é a da leitura, não a da tabela: o que mais pesa primeiro. */
const TERMOS = [
  {
    chave: "confianca",
    rotulo: "Confiança da triagem",
    ajuda:
      "O julgamento de escopo da IA, que é o único fator capaz de saber que " +
      "credenciamento de clínica está fora do escopo do Nectar. Aderência " +
      "alta vale 1,00; media, 0,75; a IA ter dito que não é relevante, zero. " +
      "Aderência baixa NÃO é o degrau mais baixo — é incerteza, e sai da conta.",
  },
  {
    chave: "afinidade",
    rotulo: "Afinidade com os atestados",
    ajuda:
      "A semelhança de lexemas entre o objeto desta licitação e os atestados " +
      "listados abaixo, reescalada. O cosseno cru vive entre 0,14 e 0,28 no " +
      "acervo, então ele é traduzido para a faixa 0–1 pela rampa configurada — " +
      "sem isso nenhuma licitação passaria de 36 numa escala de 100.",
  },
  {
    chave: "categoria",
    rotulo: "Profundidade na linha de atuação",
    ajuda:
      "Quantos atestados o Nectar tem na linha que a triagem apontou, " +
      "saturando em quatro. Zero atestados na linha vale zero, e é informação: " +
      "a IA disse 'isto é capacitação' e não há capacitação para provar.",
  },
  {
    chave: "recencia",
    rotulo: "Recência dos atestados",
    ajuda:
      "O ano dos atestados, com piso. Atestado de 2003 sustenta menos que um " +
      "de 2024, mas não vale zero: o acervo começa em 2003, esses documentos " +
      "não são recuperáveis, e continuam sendo prova de execução.",
  },
  {
    chave: "geografia",
    rotulo: "Território",
    ajuda:
      "Mesma UF entre a licitação e os atestados, com piso para outro estado — " +
      "nunca zero, porque já ter executado o serviço em outro estado continua " +
      "sendo prova de capacidade. A UF é bônus de proximidade, não requisito.",
  },
] as const;

const nd = (n: number, casas = 2) =>
  n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });

/* Duas linhas por critério: os números e, embaixo, POR QUE ele vale isso.
   A frase vem do servidor — a mesma que o PDF imprime — e é gerada em código,
   nunca por modelo: ela precisa ser exatamente a conta que foi feita. Uma
   narrativa plausível sobre a conta pode divergir dela, e a que a pessoa lê
   seria a errada. */
function Linha({ c }: { c: CriterioDoScore }) {
  const ausente = c.valor == null;
  const ajuda = TERMOS.find((t) => t.chave === c.termo)?.ajuda;
  return (
    <>
      <tr className={ausente ? css.ausente : ""}>
        <th scope="row" title={ajuda}>
          {c.rotulo}
        </th>
        <td className={css.num}>{ausente ? "—" : nd(c.valor!)}</td>
        <td className={css.peso}>×{c.peso}</td>
        <td className={css.num}>
          {c.contribui == null ? (
            <span className={css.nota}>fora da conta</span>
          ) : (
            nd(c.contribui, 1)
          )}
        </td>
      </tr>
      <tr className={css.porqueLinha}>
        <td colSpan={4} className={css.porque}>
          {c.porque}
        </td>
      </tr>
    </>
  );
}

export function PainelScore({ id }: { id: string }) {
  // MESMA queryKey do PainelAnalise, de propósito: o React Query serve as
  // duas do mesmo cache, então saber se há análise aqui não custa uma
  // requisição a mais. 404 é o caso comum e legítimo — "ninguém leu o edital".
  const temAnalise = useQuery({
    queryKey: ["analise", id],
    queryFn: () => api.analiseDe(id),
    retry: false,
  });
  const q = useQuery({
    queryKey: ["score", id],
    queryFn: () => api.scoreDe(id),
    // 404 aqui é resposta e não falha: quer dizer "não foi pontuada". Sem
    // isto o react-query tentaria de novo três vezes contra uma rota que
    // respondeu corretamente na primeira.
    retry: (falhas, erro) =>
      erro instanceof ErroApi && erro.status === 404 ? false : falhas < 2,
  });

  if (q.isPending) return <p className={css.vazio}>carregando…</p>;

  if (q.error) {
    const naoPontuada = q.error instanceof ErroApi && q.error.status === 404;
    return (
      <p className={css.vazio}>
        {naoPontuada
          ? "Esta licitação ainda não passou pela etapa de pontuação. Não é " +
            "score zero — é ausência de score, e some assim que o pipeline rodar."
          : `Não consegui carregar: ${q.error.message}`}
      </p>
    );
  }

  const j = q.data;
  // AGUARDANDO LEITURA (D17, 2026-10-05). A conta que o servidor manda é a da
  // frase do objeto — é ela que ordena a fila da leitura —, e esta tela NÃO a
  // mostra: a oportunidade fica sem score até o edital ser lido inteiro e
  // cruzado com os atestados. É o servidor quem diz que ela aguarda.
  if (j.aguardando_leitura) {
    return (
      <p className={css.vazio}>
        Aguardando leitura do edital. O score e a pré-análise desta
        oportunidade saem depois que o edital for lido inteiro e as exigências
        técnicas forem cruzadas com os atestados.
      </p>
    );
  }
  const pesoMaximo = Object.values(j.pesos).reduce((a, b) => a + b, 0);
  const parcial = j.peso_total < j.peso_minimo;
  return (
    <>
      <div className={css.topo}>
        <span className={css.valor}>
          <b>{j.valor ?? "—"}</b>
          <span>de 100</span>
        </span>
        {/* A frase vem do SERVIDOR, e não de uma conta refeita aqui. Uma
            segunda implementação da mesma explicação divergiria na primeira
            recalibração, e a divergência apareceria só para quem comparasse
            a tela com o PDF. */}
        <p className={css.explica}>{j.resumo}</p>
      </div>

      {/* A MARGEM vem antes da tabela, e antes do aviso de evidência parcial
          só quando ela é o que decide. Um `participar` com 70,0 e margem de
          0,28 ponto não é a mesma afirmação que um com 88,0 — e antes deste
          aviso os dois saíam com o mesmo selo. É a mesma doutrina de
          `peso_total`, um andar acima: o número sozinho não carrega a própria
          confiabilidade. */}
      {j.marginal && j.margem != null && (
        <p className={css.marginal}>
          <b>Por um fio.</b> Faltam {nd(Math.abs(j.margem), 2)} ponto(s) para
          esta licitação mudar de recomendação
          {j.margem >= 0
            ? " — qualquer critério que caia um pouco a derruba para “avaliar”."
            : " — falta pouco para ela virar “participar”."}{" "}
          Nem o limiar de {j.limiar} nem os pesos foram calibrados contra
          decisão humana, então uma diferença desse tamanho não distingue duas
          licitações: distingue duas escolhas de configuração.
        </p>
      )}

      {parcial && (
        <p className={css.parcial}>
          <b>Evidência parcial.</b> Este score foi apurado sobre {j.peso_total} dos{" "}
          {pesoMaximo} pontos de critério, abaixo do mínimo de {j.peso_minimo}. Por
          isso ele <b>não recomenda participar</b>, por mais alto que seja: um
          número apurado sobre metade da evidência não é comparável a um apurado
          sobre toda ela. Os critérios marcados “fora da conta” abaixo dizem o que
          faltou.
        </p>
      )}

      <table className={css.tabela}>
        <thead>
          <tr>
            <th scope="col">Critério</th>
            <th scope="col">Valor</th>
            <th scope="col">Peso</th>
            <th scope="col">Pontos</th>
          </tr>
        </thead>
        <tbody>
          {j.criterios.map((c) => (
            <Linha key={c.termo} c={c} />
          ))}
        </tbody>
      </table>

      {j.atestados.length > 0 ? (
        <>
          <h4 className={css.sub}>Atestados mais próximos</h4>
          <ul className={css.atestados}>
            {j.atestados.map((a) => (
              <li key={a.codigo}>
                {/* O selo mora junto do CÓDIGO, e não da entidade: o nome da
                    entidade é cortado com reticências quando não cabe, e o
                    aviso de que o item não é atestado não pode ser cortado. */}
                <span className={css.codigoComSelo}>
                  <span className={css.codigo}>{a.codigo}</span>
                  <SeloNaoAtestado natureza={a.natureza} />
                </span>
                <span className={css.entidade}>{a.entidade}</span>
                <span className={css.meta}>
                  {[a.uf, a.ano].filter(Boolean).join(" · ")}
                </span>
                {/* O cosseno CRU, não o reescalado. É a medição; a rampa que a
                    traduz para a escala do score é configurável, e mostrar o
                    valor já rampado exibiria um número que não existe em
                    lugar nenhum e que muda quando alguém mexe na configuração. */}
                <span className={css.sim} title="Semelhança de lexemas (cosseno de Ochiai), sem reescala">
                  {nd(a.semelhanca, 3)}
                </span>
              </li>
            ))}
          </ul>
          {/* Por extenso, e não só no `title` do selo: no toque não há
              `title`, e este é o aviso que não pode depender dele. */}
          {j.atestados.some((a) => a.natureza === "projeto") && (
            <p className={css.vazio}>
              <b>Projeto executado, sem atestado no sistema.</b> A gestão informou
              que o serviço foi executado, e por isso ele conta no score. O
              atestado não está no acervo: antes de entrar numa proposta, tem de
              ser obtido com o cliente.
            </p>
          )}
        </>
      ) : (
        <p className={css.vazio}>
          Nenhum atestado do acervo alcançou o piso de lexemas em comum. Não é
          “nenhum parecido” — é “nada acima do piso de evidência”, e é o que
          zera três dos cinco critérios acima.
        </p>
      )}

      <p className={css.rodape}>
        {/* Link direto e não fetch: o navegador abre o PDF no visualizador,
            e o cookie de sessão acompanha porque é a mesma origem. Buscar
            por fetch obrigaria a montar um blob e um object URL para ganhar
            exatamente nada. */}
        {temAnalise.data && (
          <a
            className={css.artefato}
            href={`/api/licitacoes/${id}/oportunidade.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            abrir o artefato em PDF
          </a>
        )}
        versão {j.versao} · calculado em{" "}
        {new Date(j.calculado_em).toLocaleString("pt-BR")}
      </p>
    </>
  );
}
